import { pxToRem } from '@back-office/core';
import Button from '@material-ui/core/Button';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu, { MenuProps } from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { PopoverProps } from '@material-ui/core/Popover';
import makeStyles from '@material-ui/core/styles/makeStyles';
import withStyles from '@material-ui/core/styles/withStyles';
import KeyboardArrowDownOutlinedIcon from '@material-ui/icons/KeyboardArrowDownOutlined';
import React, { MouseEvent, PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { animated, useSpring } from 'react-spring';
import GcpStatus, { TransactionStatus } from '../gcp-status';

const StyledMenu = withStyles({
  paper: {
    border: '1px solid #d3d4d5',
  },
})((props: MenuProps) => (
  <Menu
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center',
    }}
    elevation={0}
    getContentAnchorEl={null}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center',
    }}
    {...props}
  />
));

const StyledMenuItem = withStyles((theme) => ({
  root: {
    '&:focus': {
      backgroundColor: theme.palette.primary.light,
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const useStyles = makeStyles(
  (theme) => ({
    listIcon: {
      minWidth: theme.spacing(2),
    },
    icon: {
      width: pxToRem(20),
      height: pxToRem(20),
    },
    container: {
      padding: theme.spacing(1),
      border: '1px solid #C4C4C4',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: '6px',
      height: 'auto',
      color: theme.palette.common.hitGray,
      textTransform: 'capitalize',
      '& > .MuiButton-label': {
        display: 'flex',
        gap: `${theme.spacing(1)}px`,
      },
      '&:hover': {
        border: `1px solid ${theme.palette.common.ceriseRed}`,
        color: theme.palette.common.ceriseRed,
        backgroundColor: 'unset',
        '& > span > svg': {
          color: theme.palette.common.ceriseRed,
        },
      },
    },
    popoverPaper: {
      width: ({
        clientWidth,
      }: {
        clientWidth: GcpMenuBarProps<unknown>['clientWidth'];
      }) => clientWidth && `${clientWidth}px`,
    },
    textAlignCenter: {
      textAlign: 'center',
    },
    textAlignInitial: {
      textAlign: 'initial',
    },
    text: {
      color: theme.palette.common.hitGray,
    },
  }),
  { name: 'GcpMenuBar' }
);

export interface MenuBar {
  status: TransactionStatus | null;
  wording: string;
}
export interface QrTypeMenu {
  status: string | null;
  wording: string;
}

export interface GcpMenuBarProps<T> {
  name: ReactNode;
  handleOnClick: (event: MouseEvent<HTMLButtonElement>) => void;
  handleOnClose: () => void;
  anchorEl: PopoverProps['anchorEl'];
  buttonFullWidth?: boolean;
  handleOnClickMenuItem: (
    selectedItem: T | MenuBar | QrTypeMenu,
    index: number
  ) => void;
  menuBar?: MenuBar[];
  qrTypeMenu?: QrTypeMenu[];
  data?: T[];
  selected?: keyof T;
  clientWidth?: number;
}

export const GcpMenuBar = <T,>(
  props: PropsWithChildren<GcpMenuBarProps<T>>
): JSX.Element => {
  const { t } = useTranslation();

  const {
    handleOnClick,
    handleOnClose,
    anchorEl,
    name,
    buttonFullWidth,
    handleOnClickMenuItem,
    menuBar,
    qrTypeMenu,
    selected,
    data,
    clientWidth,
  } = props;
  const classes = useStyles({ clientWidth });

  const rotate = useSpring({
    config: { friction: 10 },
    transform: !anchorEl ? 'rotate(0deg)' : 'rotate(180deg)',
  });

  return (
    <>
      <Button
        aria-controls="gcp-menu-bar"
        aria-haspopup="true"
        className={classes.container}
        fullWidth={buttonFullWidth}
        onClick={handleOnClick}
      >
        {name}
        <animated.div style={{ display: 'flex', ...rotate }}>
          <KeyboardArrowDownOutlinedIcon className={classes.icon} />
        </animated.div>
      </Button>
      <StyledMenu
        disableAutoFocusItem
        keepMounted
        PopoverClasses={{ paper: classes.popoverPaper }}
        anchorEl={anchorEl}
        id="gcp-menu-bar"
        open={Boolean(anchorEl)}
        onClose={handleOnClose}
      >
        {menuBar?.map((bar, index) => (
          <StyledMenuItem
            key={bar.wording}
            onClick={() => handleOnClickMenuItem(bar, index)}
          >
            {bar.status && (
              <ListItemIcon className={classes.listIcon}>
                <GcpStatus size={8} status={bar.status} />
              </ListItemIcon>
            )}
            <ListItemText className={classes.text} primary={t(bar.wording)} />
          </StyledMenuItem>
        ))}
        {data?.map((item, index) => (
          <StyledMenuItem
            key={`${item[selected]}`}
            onClick={() => handleOnClickMenuItem(item, index)}
          >
            <ListItemText
              className={classes.text}
              primary={`${item[selected]}`}
            />
          </StyledMenuItem>
        ))}
        {qrTypeMenu?.map((bar, index) => (
          <StyledMenuItem
            key={bar.wording}
            onClick={() => handleOnClickMenuItem(bar, index)}
          >
            <ListItemText className={classes.text} primary={t(bar.wording)} />
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </>
  );
};
